import { UserLogoutProps } from '@msc-b2c/interfaces/types';
import styles from '../userLogoutWrapper.module.scss';
import { Button, Icon, Text } from '@msc-b2c/shared-components';
import {
  breakpoints,
  clearCart,
  getActiveSession,
  removeAllFullLoginSessions,
  removeExpiredLightLoginSessions,
  removeSessionsOfCurrentTab,
  replacePlaceholders,
  useWindowSize,
} from '@msc-b2c/shared-utils';
import { useMsal } from '@azure/msal-react';
import { useDispatch } from 'react-redux';

const UserLogout: React.FC<UserLogoutProps> = ({
  userIcon,
  usernameLabel,
  logoutIcon,
  fullLogoutLabel,
  lightLogoutLabel,
  logoutLink,
}) => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const isMobile = width < breakpoints.md;

  const { name, surname, loginMode } = getActiveSession();
  const isFullLogin = loginMode === 'full';
  const { instance } = useMsal();

  const onClickLogout = () => {
    // Order matters: wipe the session BEFORE dispatching clearCart() so the
    // re-render that follows reads an empty getActiveSession() and every
    // subscribed useGetBookingQuery / useGetCheckinsQuery hook switches to
    // skipToken — which RTK Query treats as an unsubscribe (no refetch).
    //
    // Do NOT dispatch bookingApi.util.resetApiState() / checkinApi.util.
    // resetApiState() here: resetApiState forces every currently-subscribed
    // query to refetch with its CAPTURED arg (e.g. the still-valid booking
    // id "ABC123"). That refetch is enqueued synchronously, then fires on
    // the next microtask — by which time the session is wiped, so the
    // request goes out without a bearer token and MuleSoft returns 400 on
    // `/v1/bookings/ABC123`.
    if (isFullLogin) {
      // Remove all full-login sessions across every tab.
      removeAllFullLoginSessions();
    } else {
      // Remove this tab's light session and any stale expired ones.
      removeSessionsOfCurrentTab();
      removeExpiredLightLoginSessions();
    }
    dispatch(clearCart());

    if (logoutLink?.url) {
      if (isFullLogin) {
        instance.logoutRedirect({
          postLogoutRedirectUri: logoutLink?.url,
        });
      } else {
        window.location.href = logoutLink.url;
      }
    }
  };

  return (
    <div className={styles.userLogoutContainer}>
      {!isMobile && (
        <>
          <div className={styles.username}>
            <Icon icon={userIcon} />
            {name && surname && (
              <Text
                text={replacePlaceholders(usernameLabel, { name, surname })}
                tagHtml="p"
              />
            )}
          </div>
          <div className={styles.divider} />
        </>
      )}
      <div className={styles.logoutButtonContainer}>
        <Button
          text={isFullLogin ? fullLogoutLabel : lightLogoutLabel}
          icon={logoutIcon}
          styleOption={styles.logoutButton}
          onClick={onClickLogout}
        />
      </div>
    </div>
  );
};

export default UserLogout;
